import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ConfirmDialog } from './ConfirmDialog';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DotsSixVertical, Trash, Plus, ArrowSquareOut, Image,
  FileText, X, CaretDown, FolderSimple, PencilSimple,
  File, FilePdf, ArrowLeft, ArrowRight, MagnifyingGlass,
} from '@phosphor-icons/react';

/* ── Types ── */

interface MenuItem {
  id: string;
  label: string;
  section_slug: string | null;
  external_url: string | null;
  sort_order: number;
  visible: boolean;
  menu_item_type: string;
  parent_id: string | null;
  page_id: string | null;
  layout_type: string;
  published: boolean;
  updated_at: string | null;
}

type ItemType = 'gallery' | 'text' | 'category' | 'external';

/* ── Constants ── */

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.15)',
  borderStrong: 'rgba(247,246,242,0.25)',
  bgHover: 'rgba(247,246,242,0.06)',
  bgSubtle: 'rgba(247,246,242,0.03)',
  bgActive: 'rgba(247,246,242,0.08)',
  danger: '#FF3F2D',
  published: '#00E87A',
};

function slugify(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diff = Date.now() - then;
  const min = 60 * 1000;
  const hour = 60 * min;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;
  if (diff < min) return 'agora';
  if (diff < hour) return `há ${Math.floor(diff / min)}min`;
  if (diff < day) return `há ${Math.floor(diff / hour)}h`;
  if (diff < week) return `há ${Math.floor(diff / day)}d`;
  if (diff < month) return `há ${Math.floor(diff / week)}sem`;
  if (diff < year) return `há ${Math.floor(diff / month)}mes`;
  return `há ${Math.floor(diff / year)}a`;
}

function typeIcon(type: string, size = 14) {
  if (type === 'gallery' || type === 'section') return <Image size={size} weight="thin" />;
  if (type === 'text' || type === 'page') return <File size={size} weight="thin" />;
  if (type === 'category') return <FolderSimple size={size} weight="thin" />;
  if (type === 'external') return <ArrowSquareOut size={size} weight="thin" />;
  return <File size={size} weight="thin" />;
}

function typeLabel(type: string) {
  if (type === 'gallery') return 'GALERIA';
  if (type === 'text' || type === 'page') return 'PÁGINA';
  if (type === 'category') return 'GRUPO';
  if (type === 'external') return 'EXTERNO';
  return 'SEÇÃO';
}

/* ── Sortable Item Row (table style) ── */

function SortableMenuRow({
  item,
  isChild,
  isFirstItem,
  mediaCount,
  onDelete,
  onEdit,
  onOpen,
  onAddChild,
  onRename,
  onIndent,
  onOutdent,
  childCount,
  isExpanded,
  onToggle,
}: {
  item: MenuItem;
  isChild: boolean;
  isFirstItem: boolean;
  mediaCount: number | null;
  onDelete: () => void;
  onEdit: () => void;
  onOpen: () => void;
  onAddChild?: () => void;
  onRename: (newLabel: string) => void;
  onIndent: () => void;
  onOutdent: () => void;
  childCount?: number;
  isExpanded?: boolean;
  onToggle?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(item.label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setEditLabel(item.label);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [editing]);

  const commitRename = () => {
    const trimmed = editLabel.trim();
    if (trimmed && trimmed !== item.label) onRename(trimmed);
    setEditing(false);
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : 0,
  };

  const isCategory = item.menu_item_type === 'category';
  const isGallery = item.menu_item_type === 'gallery';
  const showPubDot = isGallery || item.menu_item_type === 'text' || item.menu_item_type === 'page';

  const cellStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 10,
    color: C.fgMuted,
    letterSpacing: '0.05em',
  };

  return (
    <div ref={setNodeRef} style={style} className="group">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: `0.5px solid ${C.border}`,
          paddingLeft: isChild ? 24 : 0,
          minHeight: 44,
        }}
      >
        {/* Drag handle */}
        <div
          {...listeners}
          {...attributes}
          style={{ flexShrink: 0, cursor: 'grab', padding: '12px 10px', color: C.fgMuted }}
        >
          <DotsSixVertical size={14} weight="thin" />
        </div>

        {/* Name */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, paddingRight: 8 }}>
          <span style={{ flexShrink: 0, color: C.fgMuted }}>{typeIcon(item.menu_item_type)}</span>
          {editing ? (
            <input
              ref={inputRef}
              value={editLabel}
              onChange={e => setEditLabel(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setEditing(false);
              }}
              onClick={e => e.stopPropagation()}
              style={{
                fontFamily: monoFont, color: C.fg, fontSize: 10.5, textTransform: 'uppercase',
                backgroundColor: 'rgba(247,246,242,0.08)',
                border: `0.5px solid ${C.borderStrong}`,
                padding: '2px 6px', outline: 'none', flex: 1, minWidth: 0,
              }}
            />
          ) : (
            <button
              onClick={() => setEditing(true)}
              style={{
                background: 'none', border: 'none', cursor: 'text', padding: 0,
                fontFamily: monoFont, color: C.fg, fontSize: 10.5,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textAlign: 'left', flex: 1, minWidth: 0,
              }}
              title={item.label}
            >
              {item.label}
            </button>
          )}
          {isCategory && (
            <button
              onClick={onToggle}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', color: C.fgMuted,
                padding: '2px 4px', display: 'flex', alignItems: 'center', gap: 4,
                fontFamily: monoFont, fontSize: 9,
              }}
              title="expandir grupo"
            >
              <span style={{ opacity: 0.6 }}>{childCount || 0}</span>
              <span
                style={{
                  transition: 'transform 0.15s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'inline-flex',
                }}
              >
                <CaretDown size={10} weight="thin" />
              </span>
            </button>
          )}
        </div>

        {/* Mídias */}
        <div style={{ ...cellStyle, width: 70, textAlign: 'right', paddingRight: 10, flexShrink: 0 }}>
          {isGallery ? (mediaCount ?? 0) : '—'}
        </div>

        {/* Atualizada */}
        <div style={{ ...cellStyle, width: 96, textAlign: 'right', paddingRight: 10, flexShrink: 0 }}>
          {relativeTime(item.updated_at)}
        </div>

        {/* Layout */}
        <div style={{ width: 70, textAlign: 'center', flexShrink: 0 }}>
          {isGallery ? (
            <span
              style={{
                display: 'inline-block',
                fontFamily: monoFont, fontSize: 8,
                letterSpacing: '0.15em', textTransform: 'uppercase',
                color: C.fgMuted,
                border: `0.5px solid ${C.border}`,
                padding: '2px 6px',
              }}
            >
              {item.layout_type || 'A'}
            </span>
          ) : (
            <span style={cellStyle}>—</span>
          )}
        </div>

        {/* Pub */}
        <div style={{ width: 44, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
          {showPubDot ? (
            <span
              style={{
                width: 6, height: 6,
                backgroundColor: item.published ? C.published : 'transparent',
                border: item.published ? 'none' : `0.5px solid ${C.fgMuted}`,
                display: 'inline-block',
              }}
              title={item.published ? 'Publicado' : 'Rascunho'}
            />
          ) : (
            <span style={cellStyle}>—</span>
          )}
        </div>

        {/* Actions */}
        <div
          style={{
            width: 80, flexShrink: 0,
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2,
            paddingRight: 8,
          }}
        >
          <div
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{ display: 'flex', alignItems: 'center', gap: 2 }}
          >
            {!isChild && !isFirstItem && (
              <button
                onClick={e => { e.stopPropagation(); onIndent(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.fgMuted, padding: 4 }}
                title="tornar filho"
              >
                <ArrowRight size={11} weight="thin" />
              </button>
            )}
            {isChild && (
              <button
                onClick={e => { e.stopPropagation(); onOutdent(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.fgMuted, padding: 4 }}
                title="mover para raiz"
              >
                <ArrowLeft size={11} weight="thin" />
              </button>
            )}
            {isCategory && !isChild && onAddChild && (
              <button
                onClick={e => { e.stopPropagation(); onAddChild(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.fgMuted, padding: 4 }}
                title="adicionar subitem"
              >
                <Plus size={11} weight="thin" />
              </button>
            )}
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 4 }}
              title="remover"
            >
              <Trash size={11} weight="thin" />
            </button>
          </div>
          {(isGallery || item.menu_item_type === 'text' || item.menu_item_type === 'page') && (
            <button
              onClick={e => { e.stopPropagation(); onOpen(); }}
              style={{
                background: 'none', border: `0.5px solid ${C.border}`,
                cursor: 'pointer', color: C.fg, padding: '3px 8px',
                fontFamily: monoFont, fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase',
              }}
              title="editar página"
            >
              EDITAR
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Action Card (Rail) ── */

function ActionCard({
  icon,
  label,
  sublabel,
  onClick,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 10px',
        border: `0.5px solid ${active ? 'rgba(247,246,242,0.35)' : 'rgba(247,246,242,0.12)'}`,
        background: active ? 'rgba(247,246,242,0.05)' : 'transparent',
        color: active ? 'rgba(247,246,242,0.90)' : 'rgba(247,246,242,0.55)',
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        textAlign: 'left',
        width: '100%',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(247,246,242,0.35)';
        e.currentTarget.style.backgroundColor = 'rgba(247,246,242,0.05)';
        e.currentTarget.style.color = 'rgba(247,246,242,0.90)';
      }}
      onMouseLeave={e => {
        if (active) return;
        e.currentTarget.style.borderColor = 'rgba(247,246,242,0.12)';
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = 'rgba(247,246,242,0.55)';
      }}
    >
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span>{label}</span>
        <span style={{ fontSize: 8, opacity: 0.45, textTransform: 'lowercase', letterSpacing: '0.05em' }}>
          {sublabel}
        </span>
      </span>
    </button>
  );
}

/* ── Filter Chip ── */

function FilterChip({
  label,
  active,
  onClick,
  count,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        padding: '4px 9px',
        border: `0.5px solid ${active ? 'transparent' : 'rgba(247,246,242,0.15)'}`,
        background: active ? 'rgba(247,246,242,0.90)' : 'transparent',
        color: active ? '#0A0A0A' : 'rgba(247,246,242,0.60)',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label}{count !== undefined ? ` ${count}` : ''}
    </button>
  );
}

/* ── Main Component ── */

export function AdminMenuManager() {
  const { session } = useAuth();
  const [items, setItems] = useState<MenuItem[]>([]);
  const [mediaCounts, setMediaCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editItem, setEditItem] = useState<MenuItem | null>(null);

  // Search + filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pub' | 'draft'>('all');
  const [layoutFilter, setLayoutFilter] = useState<string | null>(null);

  // Inline forms state
  const [showExtForm, setShowExtForm] = useState(false);
  const [extName, setExtName] = useState('');
  const [extUrl, setExtUrl] = useState('');

  const [showPdfForm, setShowPdfForm] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfName, setPdfName] = useState('');
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const topItems = useMemo(() => items.filter(i => !i.parent_id).sort((a, b) => a.sort_order - b.sort_order), [items]);
  const childrenOf = (parentId: string) => items.filter(i => i.parent_id === parentId).sort((a, b) => a.sort_order - b.sort_order);

  const matchesFilters = (item: MenuItem) => {
    if (search.trim()) {
      if (!normalize(item.label).includes(normalize(search.trim()))) return false;
    }
    if (statusFilter !== 'all') {
      const hasPubField = item.menu_item_type === 'gallery' || item.menu_item_type === 'text' || item.menu_item_type === 'page';
      if (!hasPubField) return false;
      if (statusFilter === 'pub' && !item.published) return false;
      if (statusFilter === 'draft' && item.published) return false;
    }
    if (layoutFilter) {
      if (item.menu_item_type !== 'gallery') return false;
      if ((item.layout_type || 'A') !== layoutFilter) return false;
    }
    return true;
  };

  const sortedList = useMemo(() => {
    const result: (MenuItem & { isChild: boolean })[] = [];
    topItems.forEach(item => {
      const topMatches = matchesFilters(item);
      const children = item.menu_item_type === 'category' ? childrenOf(item.id) : [];
      const matchingChildren = children.filter(matchesFilters);
      const isExpanded = expandedGroups.has(item.id);

      if (topMatches || matchingChildren.length > 0) {
        result.push({ ...item, isChild: false });
        if (item.menu_item_type === 'category' && isExpanded) {
          const list = search.trim() || statusFilter !== 'all' || layoutFilter ? matchingChildren : children;
          list.forEach(child => result.push({ ...child, isChild: true }));
        }
      }
    });
    return result;
  }, [topItems, items, expandedGroups, search, statusFilter, layoutFilter]);

  const visibleCount = sortedList.length;

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchItems = async () => {
    if (!session) return;
    const { data } = await supabase
      .from('menu_items')
      .select('*')
      .eq('user_id', session.user.id)
      .order('sort_order');
    const list = ((data || []) as any[]).map(d => ({
      ...d,
      menu_item_type: d.menu_item_type || 'section',
      parent_id: d.parent_id || null,
      page_id: d.page_id || null,
      layout_type: d.layout_type || 'A',
      published: d.published ?? false,
      updated_at: d.updated_at || d.created_at || null,
    })) as MenuItem[];
    setItems(list);

    // media counts for galleries
    const galleryIds = list.filter(i => i.menu_item_type === 'gallery').map(i => i.id);
    if (galleryIds.length > 0) {
      const { data: mc } = await supabase
        .from('menu_gallery_images')
        .select('menu_item_id')
        .in('menu_item_id', galleryIds);
      const counts: Record<string, number> = {};
      ((mc || []) as any[]).forEach(r => {
        const id = r.menu_item_id as string;
        counts[id] = (counts[id] || 0) + 1;
      });
      setMediaCounts(counts);
    } else {
      setMediaCounts({});
    }

    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, [session]);

  /* ── DnD handler ── */
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedList.findIndex(i => i.id === active.id);
    const newIndex = sortedList.findIndex(i => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const movingItem = sortedList[oldIndex];
    const targetItem = sortedList[newIndex];
    if (movingItem.parent_id !== targetItem.parent_id) return;

    const siblings = items.filter(i => i.parent_id === movingItem.parent_id).sort((a, b) => a.sort_order - b.sort_order);
    const sibOld = siblings.findIndex(i => i.id === active.id);
    const sibNew = siblings.findIndex(i => i.id === over.id);
    if (sibOld === -1 || sibNew === -1) return;

    const reordered = arrayMove(siblings, sibOld, sibNew);
    const updated = items.map(item => {
      const idx = reordered.findIndex(r => r.id === item.id);
      if (idx !== -1) return { ...item, sort_order: idx };
      return item;
    });
    setItems(updated);

    await Promise.all(reordered.map((item, i) =>
      supabase.from('menu_items').update({ sort_order: i } as any).eq('id', item.id)
    ));
  };

  /* ── CRUD ── */
  const addItem = async (label: string, type: ItemType, externalUrl: string | null, parentId: string | null) => {
    if (!session) return;
    const siblings = items.filter(i => i.parent_id === parentId);
    const baseSlug = slugify(label) || 'pagina';
    const taken = new Set(items.map(i => i.section_slug).filter(Boolean) as string[]);
    let slug = baseSlug;
    let n = 2;
    while (taken.has(slug)) {
      slug = `${baseSlug}-${n}`;
      n += 1;
    }


    let pageId: string | null = null;

    // For 'text' type, create a free_pages record
    if (type === 'text') {
      const { data: freePage } = await supabase
        .from('free_pages')
        .insert({
          user_id: session.user.id,
          title: label.trim(),
          slug,
          published: false,
          content_json: [],
        } as any)
        .select('id')
        .single();
      if (freePage) pageId = (freePage as any).id;
    }

    await supabase.from('menu_items').insert({
      user_id: session.user.id,
      label: label.trim(),
      section_slug: type === 'text' ? slug : type === 'gallery' ? slug : null,
      external_url: type === 'external' ? externalUrl : null,
      sort_order: siblings.length,
      menu_item_type: type,
      parent_id: parentId,
      page_id: pageId,
      published: false,
      layout_type: 'A',
    } as any).select('id').single();

    await fetchItems();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  const deleteItem = async (id: string) => {
    const item = items.find(i => i.id === id);
    const children = items.filter(i => i.parent_id === id);
    for (const child of children) {
      if ((child.menu_item_type === 'text' || child.menu_item_type === 'page') && child.page_id) {
        await supabase.from('free_pages').delete().eq('id', child.page_id);
      }
      await supabase.from('menu_items').delete().eq('id', child.id);
    }

    if (item && (item.menu_item_type === 'text' || item.menu_item_type === 'page') && item.page_id) {
      await supabase.from('free_pages').delete().eq('id', item.page_id);
    }

    await supabase.from('menu_items').delete().eq('id', id);
    setDeleteTarget(null);
    fetchItems();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  const handleItemOpen = (item: MenuItem) => {
    if (item.menu_item_type === 'gallery') {
      window.dispatchEvent(new CustomEvent('cms-navigate', { detail: { tab: 'menu-gallery', projectId: item.id } }));
    } else if (item.menu_item_type === 'text' || item.menu_item_type === 'page') {
      if (item.page_id) {
        window.dispatchEvent(new CustomEvent('cms-navigate', { detail: { tab: 'freepage', projectId: item.page_id } }));
      }
    }
  };

  const renameItem = async (id: string, newLabel: string) => {
    await supabase.from('menu_items').update({ label: newLabel } as any).eq('id', id);
    fetchItems();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  const indentItem = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (!item || item.parent_id) return;
    const idx = topItems.findIndex(i => i.id === id);
    if (idx <= 0) return;
    const prev = topItems[idx - 1];
    if (!prev || prev.menu_item_type === 'category') return;
    await supabase.from('menu_items').update({ parent_id: prev.id } as any).eq('id', id);
    await fetchItems();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  const outdentItem = async (id: string) => {
    await supabase.from('menu_items').update({ parent_id: null } as any).eq('id', id);
    await fetchItems();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  /* ── Inline form handlers ── */

  const handleGalleryClick = () => {
    setShowExtForm(false);
    setShowPdfForm(false);
    addItem('nova galeria', 'gallery', null, null);
  };

  const handleTextClick = () => {
    setShowExtForm(false);
    setShowPdfForm(false);
    addItem('nova página', 'text', null, null);
  };

  const handleExtClick = () => {
    setShowPdfForm(false);
    setShowExtForm(v => !v);
  };

  const handlePdfClick = () => {
    setShowExtForm(false);
    setShowPdfForm(v => !v);
  };

  const cancelExtForm = () => {
    setShowExtForm(false);
    setExtName('');
    setExtUrl('');
  };

  const submitExtForm = () => {
    if (!extName.trim() || !extUrl.trim()) return;
    addItem(extName.trim(), 'external', extUrl.trim(), null);
    cancelExtForm();
  };

  const cancelPdfForm = () => {
    setShowPdfForm(false);
    setPdfFile(null);
    setPdfName('');
    setPdfError(null);
  };

  const submitPdfForm = async () => {
    if (!pdfName.trim() || !pdfFile || !session) return;
    setPdfUploading(true);
    setPdfError(null);
    try {
      const safeName = pdfFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${session.user.id}/pdfs/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from('site-assets')
        .upload(path, pdfFile, { upsert: false, contentType: 'application/pdf' });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('site-assets').getPublicUrl(path);
      await addItem(pdfName.trim(), 'external', urlData.publicUrl, null);
      cancelPdfForm();
    } catch (e: any) {
      setPdfError('erro ao enviar pdf.');
    } finally {
      setPdfUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-12 animate-pulse" style={{ backgroundColor: 'rgba(247,246,242,0.06)' }} />
        ))}
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 12,
    background: 'transparent',
    border: 'none',
    borderBottom: '0.5px solid rgba(247,246,242,0.25)',
    color: C.fg,
    padding: '6px 0',
    outline: 'none',
    width: '100%',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 9,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'rgba(247,246,242,0.40)',
    display: 'block',
    marginBottom: 4,
  };

  const btnCancel: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    padding: '6px 10px',
    border: '0.5px solid rgba(247,246,242,0.25)',
    background: 'transparent',
    color: C.fg,
    opacity: 0.5,
    cursor: 'pointer',
  };

  const btnPrimary = (disabled: boolean): React.CSSProperties => ({
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    padding: '6px 10px',
    border: '0.5px solid #F7F6F2',
    background: '#F7F6F2',
    color: '#0A0A0A',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.35 : 1,
  });

  const headerCellStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 8,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'rgba(247,246,242,0.22)',
  };

  const extForm = showExtForm && (
    <div
      style={{
        padding: '10px 4px 14px 4px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div>
        <label style={labelStyle}>NOME</label>
        <input autoFocus value={extName} onChange={e => setExtName(e.target.value)} style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>URL</label>
        <input value={extUrl} onChange={e => setExtUrl(e.target.value)} placeholder="https://..." style={inputStyle} />
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={cancelExtForm} style={btnCancel}>CANCELAR</button>
        <button
          onClick={submitExtForm}
          disabled={!extName.trim() || !extUrl.trim()}
          style={btnPrimary(!extName.trim() || !extUrl.trim())}
        >
          ADICIONAR
        </button>
      </div>
    </div>
  );

  const pdfForm = showPdfForm && (
    <div
      style={{
        padding: '10px 4px 14px 4px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div>
        <label style={labelStyle}>NOME</label>
        <input autoFocus value={pdfName} onChange={e => setPdfName(e.target.value)} style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>ARQUIVO</label>
        <button
          onClick={() => pdfInputRef.current?.click()}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px',
            border: '0.5px dashed rgba(247,246,242,0.25)',
            background: 'transparent', cursor: 'pointer',
            width: '100%', fontFamily: monoFont, textAlign: 'left',
          }}
        >
          <FilePdf size={14} weight="thin" style={{ color: C.fg, flexShrink: 0 }} />
          <span
            style={{
              fontSize: 11,
              color: pdfFile ? C.fg : 'rgba(247,246,242,0.4)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
            }}
          >
            {pdfFile ? pdfFile.name : 'selecionar pdf'}
          </span>
        </button>
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) {
              if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
                setPdfError('arquivo precisa ser um pdf.');
                return;
              }
              setPdfError(null);
              setPdfFile(f);
            }
            e.target.value = '';
          }}
        />
        {pdfError && (
          <span style={{ fontSize: 10, color: '#FF3F2D', fontFamily: monoFont, display: 'block', marginTop: 6 }}>
            {pdfError}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={cancelPdfForm} style={btnCancel}>CANCELAR</button>
        <button
          onClick={submitPdfForm}
          disabled={!pdfName.trim() || !pdfFile || pdfUploading}
          style={btnPrimary(!pdfName.trim() || !pdfFile || pdfUploading)}
        >
          {pdfUploading ? 'ENVIANDO...' : 'ADICIONAR'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Remover item do menu"
        description="Tem certeza? Isso também removerá o conteúdo associado (imagens/texto)."
        confirmLabel="Remover"
        destructive
        onConfirm={() => { if (deleteTarget) deleteItem(deleteTarget); }}
      />

      {editItem && (
        <EditItemModal
          item={editItem}
          onSave={async (newLabel) => {
            await renameItem(editItem.id, newLabel);
            setEditItem(null);
          }}
          onClose={() => setEditItem(null)}
          onNavigate={() => {
            handleItemOpen(editItem);
            setEditItem(null);
          }}
        />
      )}

      {/* ── RAIL ── */}
      <div
        style={{
          width: 180,
          flexShrink: 0,
          borderRight: '0.5px solid rgba(247,246,242,0.08)',
          padding: '16px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 8,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(247,246,242,0.22)',
            padding: '2px 4px 6px 4px',
          }}
        >
          CRIAR
        </div>

        <ActionCard
          icon={<Image weight="thin" size={16} />}
          label="GALERIA"
          sublabel="imagens e vídeos"
          onClick={handleGalleryClick}
        />
        <ActionCard
          icon={<FileText weight="thin" size={16} />}
          label="TEXTO"
          sublabel="sobre, manifesto..."
          onClick={handleTextClick}
        />
        <ActionCard
          icon={<ArrowSquareOut weight="thin" size={16} />}
          label="LINK EXTERNO"
          sublabel="instagram, email..."
          onClick={handleExtClick}
          active={showExtForm}
        />
        {extForm}
        <ActionCard
          icon={<FilePdf weight="thin" size={16} />}
          label="PDF"
          sublabel="upload de arquivo"
          onClick={handlePdfClick}
          active={showPdfForm}
        />
        {pdfForm}

        <div
          style={{
            marginTop: 'auto',
            paddingTop: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontFamily: monoFont,
            fontSize: 9,
            color: 'rgba(247,246,242,0.16)',
          }}
        >
          <span>↕ arraste linhas para reordenar o menu do site</span>
          <span>✎ editar abre a página em tela cheia</span>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Search */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '0.5px solid rgba(247,246,242,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <MagnifyingGlass size={14} weight="thin" style={{ color: C.fgMuted, flexShrink: 0 }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="buscar páginas..."
            style={{
              flex: 1,
              fontFamily: monoFont,
              fontSize: 11,
              background: 'transparent',
              border: 'none',
              borderBottom: '0.5px solid rgba(247,246,242,0.20)',
              color: C.fg,
              padding: '4px 0',
              outline: 'none',
            }}
          />
        </div>

        {/* Filters */}
        <div
          style={{
            padding: '10px 20px',
            borderBottom: '0.5px solid rgba(247,246,242,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <FilterChip label="TODAS" active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
          <FilterChip label="PUBLICADAS" active={statusFilter === 'pub'} onClick={() => setStatusFilter('pub')} />
          <FilterChip label="RASCUNHOS" active={statusFilter === 'draft'} onClick={() => setStatusFilter('draft')} />
          <span style={{ width: 1, height: 14, background: 'rgba(247,246,242,0.15)', margin: '0 4px' }} />
          {(['A', 'B', 'C', 'D'] as const).map(l => (
            <FilterChip
              key={l}
              label={`LAYOUT ${l}`}
              active={layoutFilter === l}
              onClick={() => setLayoutFilter(layoutFilter === l ? null : l)}
            />
          ))}
          <span
            style={{
              marginLeft: 'auto',
              fontFamily: monoFont,
              fontSize: 9,
              color: 'rgba(247,246,242,0.30)',
              letterSpacing: '0.1em',
            }}
          >
            {visibleCount} {visibleCount === 1 ? 'página' : 'páginas'}
          </span>
        </div>

        {/* Table header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: '0.5px solid rgba(247,246,242,0.08)',
          }}
        >
          <div style={{ width: 34, flexShrink: 0 }} />
          <div style={{ ...headerCellStyle, flex: 1, minWidth: 0, paddingRight: 8 }}>NOME</div>
          <div style={{ ...headerCellStyle, width: 70, textAlign: 'right', paddingRight: 10, flexShrink: 0 }}>MÍDIAS</div>
          <div style={{ ...headerCellStyle, width: 96, textAlign: 'right', paddingRight: 10, flexShrink: 0 }}>ATUALIZADA</div>
          <div style={{ ...headerCellStyle, width: 70, textAlign: 'center', flexShrink: 0 }}>LAYOUT</div>
          <div style={{ ...headerCellStyle, width: 44, textAlign: 'center', flexShrink: 0 }}>PUB.</div>
          <div style={{ width: 80, flexShrink: 0 }} />
        </div>

        {/* List */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {sortedList.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
              <FolderSimple size={24} weight="thin" style={{ color: C.fgMuted }} />
              <p
                className="text-[11px] uppercase text-center leading-relaxed"
                style={{ fontFamily: monoFont, color: C.fgMuted }}
              >
                {items.length === 0
                  ? <>nenhuma página ainda.<br />use os cards à esquerda para criar.</>
                  : 'nenhuma página encontrada com estes filtros.'}
              </p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortedList.map(i => i.id)} strategy={verticalListSortingStrategy}>
                {sortedList.map((item) => {
                  const isCategory = item.menu_item_type === 'category' && !item.isChild;
                  const children = isCategory ? childrenOf(item.id) : [];
                  const topIdx = topItems.findIndex(t => t.id === item.id);

                  return (
                    <div key={item.id}>
                      <SortableMenuRow
                        item={item}
                        isChild={item.isChild}
                        isFirstItem={!item.isChild && topIdx === 0}
                        mediaCount={item.menu_item_type === 'gallery' ? (mediaCounts[item.id] || 0) : null}
                        onEdit={() => setEditItem(item)}
                        onOpen={() => handleItemOpen(item)}
                        onDelete={() => setDeleteTarget(item.id)}
                        onAddChild={
                          isCategory
                            ? () => addItem('nova página', 'text', null, item.id)
                            : undefined
                        }
                        onRename={(newLabel) => renameItem(item.id, newLabel)}
                        onIndent={() => indentItem(item.id)}
                        onOutdent={() => outdentItem(item.id)}
                        childCount={isCategory ? children.length : undefined}
                        isExpanded={expandedGroups.has(item.id)}
                        onToggle={() => toggleGroup(item.id)}
                      />

                      {isCategory && expandedGroups.has(item.id) && children.length === 0 && (
                        <div
                          className="py-3 px-8 text-center"
                          style={{
                            borderBottom: `0.5px solid ${C.border}`,
                            borderLeft: `2px solid ${C.border}`,
                            marginLeft: 24,
                          }}
                        >
                          <p
                            className="text-[10px] lowercase"
                            style={{ fontFamily: monoFont, color: C.fgMuted }}
                          >
                            nenhuma página neste grupo ainda.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Edit Item Modal ── */

function EditItemModal({
  item,
  onSave,
  onClose,
  onNavigate,
}: {
  item: MenuItem;
  onSave: (newLabel: string) => void;
  onClose: () => void;
  onNavigate: () => void;
}) {
  const [label, setLabel] = useState(item.label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const canSave = label.trim() && label.trim() !== item.label;
  const canNavigate = item.menu_item_type === 'gallery' || item.menu_item_type === 'text' || item.menu_item_type === 'page';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0A0A0A]/60" onClick={onClose} />
      <div
        className="relative w-full max-w-[440px] flex flex-col bg-[#0A0A0A] border border-[#F7F6F2]/40"
        style={{ fontFamily: monoFont }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#F7F6F2]/12">
          <span className="text-xs tracking-[0.2em] uppercase text-[#F7F6F2]">
            EDITAR ITEM
          </span>
          <button
            onClick={onClose}
            className="text-[#F7F6F2]/40 hover:text-[#F7F6F2] transition-colors bg-transparent border-none cursor-pointer"
            aria-label="fechar"
          >
            <X size={14} weight="thin" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {/* Type badge */}
          <div className="flex items-center gap-2">
            <span style={{ color: C.fgMuted }}>{typeIcon(item.menu_item_type)}</span>
            <span className="text-[10px] tracking-widest uppercase text-[#F7F6F2]/40">
              {typeLabel(item.menu_item_type)}
            </span>
          </div>

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] tracking-widest uppercase text-[#F7F6F2]/40">
              NOME NO MENU
            </label>
            <input
              ref={inputRef}
              value={label}
              onChange={e => setLabel(e.target.value)}
              className="font-mono text-sm text-[#F7F6F2] bg-transparent border-none border-b border-[#F7F6F2]/25 py-2.5 outline-none w-full focus:border-[#F7F6F2] placeholder:text-[#F7F6F2]/25 transition-colors duration-150 focus-visible:ring-0 rounded-none"
              style={{ fontFamily: monoFont }}
              onKeyDown={e => { if (e.key === 'Enter' && canSave) onSave(label.trim()); }}
            />
          </div>

          {/* External URL (read-only display) */}
          {item.menu_item_type === 'external' && item.external_url && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] tracking-widest uppercase text-[#F7F6F2]/40">
                URL
              </label>
              <p className="text-[11px] text-[#F7F6F2]/60 truncate" style={{ fontFamily: monoFont }}>
                {item.external_url}
              </p>
            </div>
          )}

          {/* Navigate button for gallery/text */}
          {canNavigate && (
            <button
              onClick={onNavigate}
              className="font-mono text-[10px] tracking-widest uppercase text-[#0057FF] hover:underline bg-transparent border-none cursor-pointer text-left self-start"
            >
              EDITAR CONTEÚDO →
            </button>
          )}

          {/* Footer */}
          <div className="flex gap-3 justify-end pt-2 border-t border-[#F7F6F2]/12">
            <button
              onClick={onClose}
              className="font-mono text-[11px] tracking-widest uppercase px-4 py-2 border border-[#F7F6F2]/25 bg-transparent text-[#F7F6F2] opacity-50 hover:opacity-100 transition-all duration-150"
            >
              CANCELAR
            </button>
            <button
              onClick={() => onSave(label.trim())}
              disabled={!canSave}
              className="font-mono text-[11px] tracking-widest uppercase px-4 py-2 bg-[#F7F6F2] text-[#0A0A0A] border border-[#F7F6F2] hover:bg-transparent hover:text-[#F7F6F2] transition-colors duration-150 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-[#F7F6F2] disabled:hover:text-[#0A0A0A]"
            >
              SALVAR
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
